import http from "./http.js";

async function getClient(id) {
  try {
    const response = await http.get(`clients/${id}`);
    return {
      title: response.data.title,
      partnershipStopped: response.data.partnershipStopped,
      comments: response.data.comments,
      manager: response.data.manager
        ? { id: response.data.manager.id, title: response.data.manager.title }
        : null,
      analyst: response.data.analyst
        ? {
            id: response.data.analyst.id,
            title: response.data.analyst.title,
          }
        : null,
      updatesCabinetAccess: response.data.updatesCabinetAccess
        ? {
            login: response.data.updatesCabinetAccess.login,
            password: response.data.updatesCabinetAccess.password,
          }
        : null,
      projects: response.data.projects.map((p) => {
        return {
          id: p.id,
          title: p.title,
          key: p.key,
          fixPrice: p.fixPrice,
        };
      }),
      payers: response.data.payers
        ? response.data.payers.map((p) => {
            return {
              id: p.id,
              title: p.title,
            };
          })
        : null,
      partnershipStoppedDate: response.data.partnershipStoppedDate
        ? response.data.partnershipStoppedDate
        : null,
      partnershipStoppedReason: response.data.partnershipStoppedReason
        ? response.data.partnershipStoppedReason
        : null,
      googleDriveFolders: response.data.googleDriveFolders
        ? response.data.googleDriveFolders
        : [],
      url: response.data.url,
      email: response.data.email,
      crm: response.data.crm,
    };
  } catch (err) {
    throw err;
  }
}

async function getServices(id) {
  try {
    const response = await http.get(`clients/${id}/services`);
    return response.data.map((cs) => {
      return {
        service: {
          id: cs.service.id,
          title: cs.service.title,
        },
        type: {
          id: cs.type.id,
          title: cs.type.title,
        },
        recurrence: cs.recurrence,
        startFrom: cs.startFrom,
        payedTo: cs.payedTo,
        stoppedFrom: cs.stoppedFrom,
        price: cs.price,
        writeOffWay: cs.writeOffWay,
        quantity: cs.quantity,
        stoppedReason: cs.stoppedReason,
      };
    });
  } catch (err) {
    throw err;
  }
}

async function getProducts(id) {
  try {
    const response = await http.get(`clients/${id}/software`);
    return response.data.map((cs) => {
      return {
        product: {
          id: cs.product.id,
          title: cs.product.title,
        },
        regNumber: cs.regNumber,
        updateByClient: cs.updateByClient,
        version: cs.version,
        jiraIssueKey: cs.jiraIssueKey === "null" ? null : cs.jiraIssueKey,
        notes: cs.notes,
        versions: cs.versions,
        actualVersion: cs.actualVersion,
        invoice: cs.invoice
          ? {
              id: cs.invoice.id,
              title: cs.invoice.title,
            }
          : null,
        type: cs.type,
        trackVersions: cs.trackVersions,
        get isOverdue() {
          return !!this.version && this.actualVersion !== this.version;
        },
      };
    });
  } catch (err) {
    throw err;
  }
}

async function updateClient(id, client) {
  try {
    const response = await http.put(`clients/${id}`, client);
    return response;
  } catch (err) {
    throw err;
  }
}

async function createClient(client) {
  try {
    const response = await http.post("clients", client);
    return response;
  } catch (err) {
    throw err;
  }
}

async function getPayments(clientId) {
  try {
    const response = await http.get(`clients/${clientId}/payments`);
    return response.data.map((p) => {
      return {
        id: p.id,
        date: p.date,
        paymentType: p.paymentType,
        project: p.project,
        hours: p.hours,
        amount: p.amount,
        isPaymentForPackage: p.isPaymentForPackage,
      };
    });
  } catch (err) {
    throw err;
  }
}

async function getClients(
  page = 1,
  rowsPerPage = 9,
  search = "",
  filter = null,
  sort = ""
) {
  try {
    const response = await http.get("clients", {
      params: {
        page,
        pageSize: rowsPerPage,
        searchTerm: search,
        "filter.managerId": filter?.manager?.id || "",
        "filter.analystId": filter?.analyst?.id || "",
        "filter.productId": filter?.product?.id || "",
        "filter.serviceId": filter?.service?.id || "",
        ...sort,
      },
    });

    return response.data;
  } catch (err) {
    throw err;
  }
}

async function getProjects(clientId) {
  try {
    const response = await http.get(`clients/${clientId}/projects`);
    return response.data.map((p) => {
      return {
        id: p.id,
        title: p.title,
      };
    });
  } catch (err) {
    throw err;
  }
}

async function selectClient(
  page = 1,
  rowsPerPage = 9,
  search = "",
  partnershipStopped
) {
  try {
    const response = await http.get("clients/references", {
      params: {
        page,
        pageSize: rowsPerPage,
        searchTerm: search,
        partnershipStopped,
      },
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

async function getInvoices(
  clientId,
  productId,
  page = 1,
  rowsPerPage = 9,
  search = ""
) {
  try {
    const response = await http.get(`clients/${clientId}/invoices`, {
      params: {
        productId,
        page,
        pageSize: rowsPerPage,
        searchTerm: search,
      },
    });
    return response.data;
  } catch (err) {
    throw err;
  }
}

async function getSpecifications(clientId) {
  try {
    const response = await http.get(`clients/${clientId}/specifications`);
    return response.data.map((p) => {
      return {
        id: p.specificationId,
        title: p.title,
        author: p.author,
        date: p.createdAt,
        issueTrackerRef: p.issueTrackerRef,
        estimateMin: p.estimateMin,
        estimateNorm: p.estimateAvg,
        estimateMax: p.estimateMax,
        tAvg: p.tAvg,
        totalTimeSpentSeconds: p.totalTimeSpentSeconds,
        factWithGrades: p.factWithGrades,
        devProgress: p.devProgress,
      };
    });
  } catch (err) {
    throw err;
  }
}

export {
  getClient,
  getServices,
  getProducts,
  updateClient,
  createClient,
  selectClient,
  getPayments,
  getClients,
  getProjects,
  getInvoices,
  getSpecifications,
};
